import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../api/services';
import { MapPin, CheckCircle2, AlertCircle, Loader2, MailOpen } from 'lucide-react';
import toast from 'react-hot-toast';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL format: /verify-email?token=TOKEN&id=USER_ID
  const token  = searchParams.get('token') || '';
  const userId = searchParams.get('id')    || '';

  const [status, setStatus] = useState('idle'); // idle | verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const hasVerified = useRef(false);

  // Auto-verify as soon as the page loads if token + id are present
  useEffect(() => {
    if (!token || !userId || hasVerified.current) return;
    hasVerified.current = true;
    verify();
  }, [token, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const verify = async () => {
    setStatus('verifying');
    try {
      // POST /auth/verify-email
      await authService.verifyEmail(userId, token);
      setStatus('success');
      toast.success('Email verified! You can now sign in.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.message || 'Verification failed. The link may have expired.');
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResending(true);
    try {
      // POST /auth/resend-verification — always returns success
      await authService.resendVerification(resendEmail);
      setResendSent(true);
      toast.success('Verification email sent!');
    } catch {
      setResendSent(true); // still show success (security)
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 bg-forest-800 rounded-xl flex items-center justify-center">
            <MapPin size={17} className="text-forest-300" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif font-bold text-forest-900 text-lg">
              TourEnt <span className="text-rust-500">Hub</span>
            </span>
            <span className="text-[9px] font-sans uppercase tracking-[0.15em] text-forest-500">
              Tourism Entrepreneurship Hub
            </span>
          </div>
        </Link>

        {/* ── Verifying ── */}
        {status === 'verifying' && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
              <Loader2 size={32} className="text-forest-600 animate-spin" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900 mb-2">Verifying your email...</h2>
              <p className="font-sans text-sm text-forest-500">Just a moment please.</p>
            </div>
          </div>
        )}

        {/* ── Success ── */}
        {status === 'success' && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-forest-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900 mb-2">Email verified!</h2>
              <p className="font-sans text-sm text-forest-500">
                Your email has been confirmed. Redirecting you to sign in...
              </p>
            </div>
            <Link to="/login" className="btn-primary inline-flex">
              Sign In Now →
            </Link>
          </div>
        )}

        {/* ── Error ── */}
        {status === 'error' && (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-rust-50 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle size={32} className="text-rust-400" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-forest-900 mb-2">Verification failed</h2>
                <p className="font-sans text-sm text-forest-500">{errorMsg}</p>
              </div>
            </div>

            {/* Resend form */}
            {!resendSent ? (
              <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
                <p className="font-sans text-sm font-medium text-forest-800">Resend verification email</p>
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={e => setResendEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 font-sans text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
                  />
                  <button
                    type="submit"
                    disabled={resending || !resendEmail.trim()}
                    className="btn-primary w-full justify-center text-sm py-2.5 disabled:opacity-60"
                  >
                    {resending
                      ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                      : 'Resend Verification Email'}
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-forest-50 border border-forest-200 rounded-2xl p-5 text-center space-y-2">
                <CheckCircle2 size={22} className="text-forest-600 mx-auto" />
                <p className="font-sans text-sm text-forest-700 font-medium">Verification email sent!</p>
                <p className="text-xs text-forest-500">Check your inbox and click the new link.</p>
              </div>
            )}

            <div className="text-center">
              <Link to="/login" className="text-sm font-sans text-forest-500 hover:text-forest-800 transition-colors">
                Back to sign in
              </Link>
            </div>
          </div>
        )}

        {/* ── Idle (no token in URL) — manual entry fallback ── */}
        {status === 'idle' && !token && (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-forest-50 rounded-full flex items-center justify-center mx-auto">
                <MailOpen size={32} className="text-forest-500" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-forest-900 mb-2">Verify your email</h2>
                <p className="font-sans text-sm text-forest-500 leading-relaxed">
                  Check your inbox for a verification link from TourEnt Hub. Click the link to confirm your email address.
                </p>
              </div>
            </div>

            {!resendSent ? (
              <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
                <p className="font-sans text-sm font-medium text-forest-800">Didn't receive the email?</p>
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={e => setResendEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 font-sans text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
                  />
                  <button
                    type="submit"
                    disabled={resending || !resendEmail.trim()}
                    className="btn-primary w-full justify-center text-sm py-2.5 disabled:opacity-60"
                  >
                    {resending
                      ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                      : 'Resend Verification Email'}
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-forest-50 border border-forest-200 rounded-2xl p-5 text-center space-y-2">
                <CheckCircle2 size={22} className="text-forest-600 mx-auto" />
                <p className="font-sans text-sm text-forest-700 font-medium">Verification email sent!</p>
                <p className="text-xs text-forest-500">Check your inbox and click the new link.</p>
              </div>
            )}

            <div className="text-center">
              <Link to="/login" className="text-sm font-sans text-forest-500 hover:text-forest-800 transition-colors">
                Back to sign in
              </Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VerifyEmailPage;